import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { WarningAltFilled } from '@mint-lib/icons';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from '@mint-lib/tw-components';

import { useDeleteTemplateMutation } from '../../queries/index.js';
import { CheckboxWithLabel } from '../atoms/CheckboxWithLabel.jsx';

type Props = {
  templateId: string;
  onClose: () => void;
  onSuccess: () => void;
};

export const TemplateDeleteDialog = ({
  templateId,
  onClose,
  onSuccess,
}: Props) => {
  const { t } = useTranslation('@myn/import-hub');

  const [isDeletionConfirmed, setIsDeletionConfirmed] = useState(false);
  const { mutateAsync: deleteTemplate, isPending: isSubmitting } =
    useDeleteTemplateMutation();

  const handleConfirmDeleteChange = (checked: boolean) => {
    setIsDeletionConfirmed(checked);
  };

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async () => {
    try {
      await deleteTemplate(templateId);
      onSuccess();
      onClose();
    } catch {
      /* empty */
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleClose();
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot size="s">
          <DialogHeader>
            <DialogTitle className="flex flex-row items-center justify-center gap-2">
              <WarningAltFilled className="size-5 fill-error-main" />
              <p>{t('Delete this template?')}</p>
            </DialogTitle>
          </DialogHeader>
          <DialogContent className="flex-col">
            <DialogDescription>
              {t('If you proceed this template will be permanently deleted')}
            </DialogDescription>
            <div className="mt-4">
              <CheckboxWithLabel
                label={t(
                  'I understand I will not be able to recover the unique email and template mapping.',
                )}
                checked={isDeletionConfirmed}
                onCheckedChange={handleConfirmDeleteChange}
              />
            </div>
          </DialogContent>
          <DialogFooter>
            <Button size="large" variant="text" onClick={handleClose}>
              {t('Cancel')}
            </Button>
            <Button
              size="large"
              color="error"
              onClick={handleSubmit}
              disabled={isSubmitting || !isDeletionConfirmed}
            >
              {t('Yes, delete forever')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};
